import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { deepMerge } from '../../helpers/deep-merge';
import { STANDARD_REMOTE_URL_PREFIX } from '../../consts';
import { SharedModule } from '@shared/shared.module';
import { Consts } from '@shared/consts';
import {
	EmbeddedRemoteApplicationComponent,
	RemoteAppConfig,
	SlCacheEnabledDirective
} from '@itero/standard-launch-sdk';
import { KnownComponentsNames } from '@core/components-names';
import { BaseDestroyable } from '@core/base-destroyable';
import { filter, takeUntil } from 'rxjs/operators';
import { tap } from 'rxjs';
import { StickyHeaderService } from 'app/services/stickyHeaderService/stickyHeader.service';

@Component({
	selector: 'itero-remote-application',
	standalone: true,
	imports: [SharedModule, EmbeddedRemoteApplicationComponent, SlCacheEnabledDirective],
	styleUrl: 'remote-application-container.component.scss',
	templateUrl: 'remote-application-container.component.html',
})
export class RemoteApplicationContainerComponent extends BaseDestroyable implements OnInit, OnDestroy {
	isVerticalMenu = false;
	appId: string | undefined = undefined;
	appParameters: Record<string, unknown> | undefined = undefined;
	descriptor: RemoteAppConfig | undefined = undefined;
	hideContactSelection = true;
	shouldShowScreenshots = false;
	shouldShowDownloadScreenshots = false;
	shouldShowExport = false;
	isInsideContent = false;
	backToLastPage = true;

	/**
	 * App IDs that should keep their DOM when the user navigates away and back.
	 *
	 * AOHS uses a web component that tears down its 3D/WASM state on unmount; the
	 * Standard Launch SDK keeps listed apps in a fixed overlay and only hides them
	 * instead of removing them from the page.
	 *
	 * Passed once at init — do not change at runtime. Add only apps that need this.
	 */
	readonly preservedAppIds: readonly string[] = [KnownComponentsNames.AohsComponent];

	private navigationState: Record<string, unknown> | undefined = undefined;
	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly stickyHeaderService: StickyHeaderService,
	) {
		super();
		this.captureCurrentNavigationState();
		this.mapHeaderHeightToStageInset();

		this.router.events
			.pipe(
				filter((event): event is NavigationStart => event instanceof NavigationStart),
				tap(() => {
					this.captureCurrentNavigationState();
				}),
				takeUntil(this.componentAlive$),
			)
			.subscribe();
	}

	async ngOnInit(): Promise<void> {
		this.activatedRoute.paramMap
			.pipe(
				tap(() => {
					this.updateDescriptor();
				}),
				takeUntil(this.componentAlive$),
			)
			.subscribe();
	}

	async ngOnDestroy(): Promise<void> {
		super.ngOnDestroy();
		const storageKey = this.getStorageKey();
		sessionStorage.removeItem(storageKey);
	}

	public getPassedParameters(): Record<string, unknown> {
		const storageKey = this.getStorageKey();

		if (this.navigationState) {
			const { isBackNavigation: _back, ...persisted } = this.navigationState;
			sessionStorage.setItem(storageKey, JSON.stringify(persisted));
			return this.navigationState;
		}

		const storedState = sessionStorage.getItem(storageKey);
		if (!storedState) {
			return {};
		}
		const params = JSON.parse(storedState) as Record<string, unknown>;
		delete params.isBackNavigation;
		return params;
	}

	private captureCurrentNavigationState(): void {
		const nav = this.router.getCurrentNavigation();
		const state = nav?.extras?.state as Record<string, unknown> | undefined;

		// Browser Back/Forward (popstate) does not carry custom navigation state,
		// but semantically it IS a back navigation. Synthesize a state with
		// `isBackNavigation: true` so the SL SDK attempts to restore MFE.
		if (nav?.trigger === 'popstate') {
			this.navigationState = { ...state, isBackNavigation: true };
			return;
		}

		this.navigationState = state;
	}

	private getStorageKey(): string {
		return `remote-app-state`;
	}

	private updateDescriptor(): void {
		const posParamsAppId = this.activatedRoute.snapshot.paramMap.get('appId');
		const decodedAppId = posParamsAppId ? decodeURIComponent(posParamsAppId) : undefined;

		const passedParameters = this.getPassedParameters();
		const stateAppId = typeof passedParameters['appId'] === 'string' ? passedParameters['appId'] : undefined;
		const resolvedAppId = stateAppId ?? decodedAppId;

		if (!resolvedAppId) {
			return;
		}

		this.updateStickyHeaderProperties(resolvedAppId);
		this.openApplication(resolvedAppId, passedParameters);
	}

	private openApplication(appId: string, passedParameters: Record<string, unknown>): void {
		const { isBackNavigation, ...paramsWithoutNav } = passedParameters;
		const appParameters = deepMerge(
			{
				appParameters: {
					basePath: `${STANDARD_REMOTE_URL_PREFIX}/${appId}`,
				},
			},
			paramsWithoutNav,
		);

		const basePath = `${STANDARD_REMOTE_URL_PREFIX}/${appId}`;

		this.appId = appId;
		this.appParameters = appParameters;
		this.descriptor = {
			appId,
			appParameters,
			basePath,
			isBackNavigation: isBackNavigation === true,
		};

		if (appId === Consts.visitReportAppId) {
			this.isVerticalMenu = true;
		}
	}

	private updateStickyHeaderProperties(appId: string): void {
		if (appId === Consts.selfServiceAppId) {
			this.hideContactSelection = false;
		}

		if (appId === KnownComponentsNames.AohsComponent) {
			this.isVerticalMenu = true;
			this.shouldShowScreenshots = true;
			this.shouldShowDownloadScreenshots = true;
			this.shouldShowExport = true;
			this.isInsideContent = true;
		}

		if (appId === KnownComponentsNames.AxiWrapper) {
			this.isVerticalMenu = true;
			this.shouldShowScreenshots = true;
			this.shouldShowDownloadScreenshots = false;
			this.shouldShowExport = false;
			this.isInsideContent = true;
		}

		if (appId === KnownComponentsNames.IteroOrdersUi) {
			this.isVerticalMenu = false;
			this.shouldShowScreenshots = false;
			this.shouldShowDownloadScreenshots = false;
			this.shouldShowExport = false;
			this.isInsideContent = false;
			this.backToLastPage = false;
		}
	}

	/**
	 * Tell the Standard Launch SDK preserved overlay to sit below the shell header.
	 *
	 * The sticky header sets `--header-height` on the document; the SDK reads
	 * `--sl-stage-inset-top` for the same gap. Safe to call more than once.
	 */
	private mapHeaderHeightToStageInset(): void {
		document.documentElement.style.setProperty(
			'--sl-stage-inset-top',
			'var(--header-height, 0px)'
		);
	}
}
